import { pick } from '@vendure/common/lib/pick';
import { Apollo, gql } from 'apollo-angular';
import { from } from 'rxjs';
import { bufferCount, concatMap, map, switchMap } from 'rxjs/operators';

import {
  CollectionFilterParameter,
  CreateCollection,
  CreateCollectionInput,
  DeleteCollection,
  GetCollection,
  GetCollectionContents,
  GetCollectionFilters,
  GetCollectionList,
  MoveCollection,
  MoveCollectionInput,
  UpdateCollection,
  UpdateCollectionInput,
} from '../../common/generated-types';
import {
  CREATE_COLLECTION,
  DELETE_COLLECTION,
  GET_COLLECTION,
  GET_COLLECTION_CONTENTS,
  GET_COLLECTION_FILTERS,
  GET_COLLECTION_LIST,
  MOVE_COLLECTION,
  UPDATE_COLLECTION,
} from '../definitions/collection-definitions';

import { BaseDataService } from './base-data.service';

export class CollectionDataService {
  constructor(private baseDataService: BaseDataService, private apollo: Apollo) {}

  getCollectionFilters() {
    return this.baseDataService.query<GetCollectionFilters.Query>(GET_COLLECTION_FILTERS);
  }

  getCollections(take = 10, skip = 0) {
    return this.baseDataService.query<GetCollectionList.Query, GetCollectionList.Variables>(GET_COLLECTION_LIST, {
      options: {
        take,
        skip,
      },
    });
  }

  searchCollections(search: string) {
    return this.apollo
      .query({
        query: gql`
          query SearchCollections($search: String) {
            collections(options: { take: 10, filter: { name: { contains: $search } } }) {
              items {
                id
                name
              }
            }
          }
        `,
        variables: { search },
      })
      .pipe(map((payload: any) => payload.data.collections));
  }

  getCollection(id: string) {
    return this.baseDataService.query<GetCollection.Query, GetCollection.Variables>(GET_COLLECTION, {
      id,
    });
  }

  createCollection(input: CreateCollectionInput) {
    return this.baseDataService
      .mutate<CreateCollection.Mutation, CreateCollection.Variables>(CREATE_COLLECTION, {
        input: pick(input, ['translations', 'parentId', 'assetIds', 'featuredAssetId', 'filters', 'customFields']),
      })
      .pipe(switchMap((res) => this.assignAllChannelsToCollection(res.createCollection.id).pipe(map(() => res))));
  }

  updateCollection(collection: UpdateCollectionInput) {
    return this.apollo.mutate({
      mutation: gql`
        mutation UpdateCollection($collection: JSON!) {
          updateCollectionNew(collection: $collection) {
            id
          }
        }
      `,
      variables: { collection },
    });
  }

  updateCollectionFilters(id: string, filters: any) {
    return this.apollo.mutate({
      mutation: gql`
        mutation UpdateCollectionFilters($id: ID!, $filters: JSON!) {
          updateCollectionFilters(collectionID: $id, filters: $filters)
        }
      `,
      variables: { id, filters },
    });
  }

  assignAllChannelsToCollection(id: string) {
    return this.apollo.mutate({
      mutation: gql`
        mutation AssignChannelsToCollection($id: ID!) {
          assignChannelsToCollection(id: $id) {
            id
          }
        }
      `,
      variables: { id },
    });
  }

  moveCollection(inputs: MoveCollectionInput[]) {
    return from(inputs).pipe(
      concatMap((input) => this.baseDataService.mutate<MoveCollection.Mutation, MoveCollection.Variables>(MOVE_COLLECTION, { input })),
      bufferCount(inputs.length),
    );
  }

  deleteCollection(id: string) {
    return this.baseDataService.mutate<DeleteCollection.Mutation, DeleteCollection.Variables>(DELETE_COLLECTION, {
      id,
    });
  }

  getCollectionContents(id: string, take = 10, skip = 0, filterTerm?: string) {
    const filter = filterTerm ? ({ name: { contains: filterTerm } } as CollectionFilterParameter) : undefined;
    return this.baseDataService.query<GetCollectionContents.Query, GetCollectionContents.Variables>(GET_COLLECTION_CONTENTS, {
      id,
      options: {
        skip,
        take,
        filter,
      },
    });
  }
}
